import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { ToastService } from '../../../services/toast.service';

@Component({
  selector: 'app-toast-container',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  template: `
    <div
      class="fixed top-5 right-5 z-50 flex flex-col gap-2.5 pointer-events-none max-w-sm w-full px-4 sm:px-0"
      aria-live="polite"
    >
      @for (toast of toastService.toasts(); track toast.id) {
        <div
          class="pointer-events-auto flex items-center gap-3 p-3.5 rounded-2xl shadow-xl border bg-white/95 backdrop-blur-md transition-all duration-300 ease-out transform translate-y-0"
          [ngClass]="[
            toast.type === 'success' ? 'border-emerald-200 bg-emerald-50/95' : '',
            toast.type === 'error' ? 'border-rose-200 bg-rose-50/95' : '',
            toast.type === 'warning' ? 'border-amber-200 bg-amber-50/95' : '',
            toast.type === 'info' ? 'border-blue-200 bg-blue-50/95' : ''
          ]"
        >
          <!-- Icon -->
          <div
            class="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 border shadow-2xs self-center"
            [ngClass]="[
              toast.type === 'success' ? 'bg-emerald-100/90 text-emerald-600 border-emerald-200' : '',
              toast.type === 'error' ? 'bg-rose-100/90 text-rose-600 border-rose-200' : '',
              toast.type === 'warning' ? 'bg-amber-100/90 text-amber-600 border-amber-200' : '',
              toast.type === 'info' ? 'bg-blue-100/90 text-blue-600 border-blue-200' : ''
            ]"
          >
            <mat-icon class="text-xl !w-5 !h-5 flex items-center justify-center leading-none">
              @switch (toast.type) {
                @case ('success') { check_circle }
                @case ('error') { error }
                @case ('warning') { warning }
                @case ('info') { info }
              }
            </mat-icon>
          </div>

          <!-- Text -->
          <div class="flex-1 min-w-0 pr-1 self-center">
            @if (toast.title) {
              <h4
                class="text-sm font-bold tracking-tight mb-0.5"
                [ngClass]="{
                  'text-emerald-950': toast.type === 'success',
                  'text-rose-950': toast.type === 'error',
                  'text-amber-950': toast.type === 'warning',
                  'text-blue-950': toast.type === 'info'
                }"
              >
                {{ toast.title }}
              </h4>
            }
            <p
              class="text-xs leading-relaxed"
              [ngClass]="{
                'text-emerald-800 font-medium': toast.type === 'success',
                'text-rose-800 font-medium': toast.type === 'error',
                'text-amber-800 font-medium': toast.type === 'warning',
                'text-blue-800 font-medium': toast.type === 'info'
              }"
            >
              {{ toast.message }}
            </p>
          </div>

          <!-- Dismiss Button -->
          <button
            type="button"
            (click)="toastService.dismiss(toast.id)"
            class="w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-black/5 transition-colors cursor-pointer shrink-0 border-0 bg-transparent p-0 self-center"
            aria-label="Dismiss toast"
          >
            <svg class="w-4 h-4 stroke-current" viewBox="0 0 24 24" fill="none" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      }
    </div>
  `,
})
export class ToastContainerComponent {
  public toastService = inject(ToastService);
}
